import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPlug, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import StepFooter from './StepFooter';
import { startInstagramOAuth } from '../../../utils/instagramOAuth';
import './StepInstagram.css';

const API = process.env.REACT_APP_API_BASE_URL;

export default function StepInstagram({ botId, goNext, returnPath, busy: saving }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');
  const stopOAuth = useRef(null);
  useEffect(() => () => { stopOAuth.current?.(); stopOAuth.current = null; }, [botId]);
  const refresh = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const response = await fetch(`${API}auth/instagram/status?bot_id=${encodeURIComponent(botId)}`);
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.detail === 'string' ? data.detail : t('wix_instagram_status_error'));
      setStatus(data);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [botId, t]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const callbackError = new URLSearchParams(window.location.search).get('instagram_error');
    if (callbackError) setError(t(callbackError === 'cancelled' ? 'wix_instagram_cancelled' : 'wix_instagram_connect_error'));
  }, [t]);
  const connected = (status?.connections || []).filter(item => item.status === 'active');

  function connect() {
    if (connecting || saving) return;
    setConnecting(true); setError('');
    stopOAuth.current?.();
    stopOAuth.current = startInstagramOAuth({botId, returnPath, openingLabel:t('wix_instagram_connecting'),
      onStatus:setStatus, onError:message => setError(t(message)), onDone:() => setConnecting(false)});
  }
  function cancelConnect() {
    stopOAuth.current?.(); stopOAuth.current = null;
    setConnecting(false);
  }
  function connectLater() {
    cancelConnect(); goNext();
  }

  return <>
    <header className="ac-head">
      <h2 className="ac-title">{t('wix_instagram_title')}</h2>
      <p className="ac-sub">{t('wix_instagram_sub')}</p>
    </header>
    <div className="ac-body ac-instagram-body">
      <section className={`ac-instagram-account ${connected.length ? 'is-connected' : ''}`} aria-busy={loading || connecting}>
        <img className="ac-instagram-logo" src="/instagram-logo.webp" alt="Instagram" />
        <div className="ac-instagram-copy">
          <h3>{connected.length ? t('wix_instagram_connected') : t('wix_instagram_account')}</h3>
          <p>{connected.length ? connected.map(item => item.display_name || 'Instagram').join(', ') : t('wix_instagram_account_hint')}</p>
          {connected.length > 0 && <span className="ac-instagram-connected"><FontAwesomeIcon icon={faCheck} /> {t('connected')}</span>}
        </div>
        {!connected.length && <button type="button" className="ac-btn-primary" onClick={connect} disabled={loading || connecting || saving || !status?.configured}>
          {connecting ? <span className="ac-spinner ac-spinner-sm ac-spinner-light" /> : <FontAwesomeIcon icon={faPlug} />}
          {t(connecting ? 'wix_instagram_connecting' : 'wix_instagram_connect')}
        </button>}
      </section>
      {connecting && <div className="ac-instagram-status" role="status">
        <span>{t('wix_instagram_popup_waiting')}</span>
        <button type="button" className="ac-btn-ghost" onClick={cancelConnect}>{t('cancel')}</button>
      </div>}
      {loading && <div className="ac-instagram-status" role="status"><span className="ac-spinner ac-spinner-sm" />{t('loading')}</div>}
      {!loading && status && !status.configured && !connected.length && <div className="ac-error" role="alert">{t('wix_instagram_unavailable')}</div>}
      {error && <div className="ac-error" role="alert">{error}</div>}
      {(error || (!loading && !status?.configured)) && <button type="button" className="ac-btn-ghost" onClick={refresh} disabled={loading}><FontAwesomeIcon icon={faRotateRight} />{t('wix_entry_retry')}</button>}
      <div className="ac-instagram-next">
        <h3>{t('wix_instagram_next_title')}</h3>
        <ol><li>{t('wix_instagram_next_connect')}</li><li>{t('wix_instagram_next_knowledge')}</li><li>{t('wix_instagram_next_test')}</li></ol>
      </div>
    </div>
    <StepFooter primaryLabel={t('wxo_continue')} onPrimary={goNext}
      onSkip={!connected.length ? connectLater : undefined} skipLabel={t('wix_instagram_connect_later')}
      disabled={!connected.length || loading || connecting} busy={saving} />
  </>;
}
